/**
 * Hazır arama arayüzü: mağazanın arama kutusuna tıklanınca **ortada açılan modal**.
 *
 * Neden dropdown değil de modal: dropdown'ın mağaza sayfasında çarptığı her şey
 * (kapsayıcının `overflow:hidden`'ı, transform'lu ata elemanın `position:fixed`'i
 * bozması, `z-index:99999` sticky header, scroll takibi) modalda ortadan kalkıyor.
 *
 * `<dialog>.showModal()` kullanılıyor çünkü bedavaya şunları veriyor:
 * native top layer (z-index'ten bağımsız), `::backdrop`, odak tuzağı, Esc ile
 * kapanma, sayfanın kalanının `inert` olması ve `aria-modal` semantiği.
 * Desteklemeyen tarayıcıda (Safari < 15.4) `data-fallback` moduna düşülür.
 */

import type { ISearchClient, IWatchHandle, SearchState } from "./client";
import { AiSearchQueryError, type IAiSearchQueryResult } from "./client";
import { injectStyles } from "./styles";
import {
  browseView,
  highlightHtml,
  suggestView,
  type IPanelView,
} from "./suggest-view";

export interface IWidgetTexts {
  placeholder: string;
  empty: string;
  loading: string;
  errorGeneric: string;
  errorRateLimit: string;
  errorBilling: string;
  errorAuth: string;
  allResults: string;
  close: string;
  hint: string;
  /** Sol kolon: ürün adı önerileri. */
  suggestions: string;
  /** Sağ kolon başlığı (yazarken). */
  products: string;
  /** Sağ kolon başlığı (yazmadan açılışta). */
  featured: string;
  /** Öneri var ama ürün yok (yazım hatası vb.). `{q}` aranan metin. */
  noProducts: string;
  /** `noProducts` altında, solda öneri ya da grup varken. */
  noProductsHint: string;
}

const DEFAULT_TEXTS: IWidgetTexts = {
  placeholder: "Ürün ara…",
  empty: "Sonuç bulunamadı.",
  loading: "Aranıyor…",
  errorGeneric: "Arama şu an kullanılamıyor.",
  errorRateLimit: "Çok fazla arama yapıldı, birazdan tekrar deneyin.",
  errorBilling: "Arama servisi geçici olarak kullanılamıyor.",
  errorAuth: "Arama servisi bu mağaza için etkin değil.",
  allResults: "Tüm sonuçları gör",
  close: "Kapat",
  hint: "Gezinmek için ok tuşları, seçmek için Enter",
  suggestions: "Öneriler",
  products: "Ürünler",
  featured: "Öne çıkanlar",
  noProducts: "“{q}” için ürün bulunamadı.",
  noProductsHint: "Önerilerden birini seçin ya da farklı bir kelime deneyin.",
};

export interface IWidgetUiOptions {
  client: ISearchClient;
  /** Tıklanınca modal açılacak eleman(lar). Selector veya elemanın kendisi. */
  trigger?: string | Element | Element[];
  /** Kendi arama butonumuzun basılacağı kapsayıcı. */
  mount?: string | Element;
  /**
   * Ürün linki şablonu. Servis her sonuçta ürünün `url`'ini döndürüyor ve o
   * kullanılıyor; şablon yalnız `url` boş geldiğinde yedek. `{code}` ve `{id}`
   * desteklenir.
   */
  productUrl?: string;
  /**
   * "Tüm sonuçlar" bağlantısı. `{q}` sorguyla değiştirilir. Öneri ve
   * kategori/marka seçimi de buraya gider (kategori adıyla arama, servis bunu
   * o kategoriyle sınırlar). Verilmezse öneri seçimi kutuya yazılır.
   */
  searchUrl?: string;
  /** Fiyat biçimlendirme. Servis para birimi dönmüyor, buradan gelmeli. */
  currency?: string;
  locale?: string;
  /**
   * `light` / `dark` sabit; `auto` ziyaretçinin işletim sistemi tercihine
   * (`prefers-color-scheme`) uyar. Mağazanın kendi koyu tema düğmesi varsa
   * `setTheme()` ile senkron tutulur.
   */
  theme?: TWidgetTheme;
  /** Varsayılan stilleri enjekte et. `false` ise mağaza kendi CSS'ini yazar. */
  styles?: boolean;
  /** Cmd/Ctrl+K ile açma. `false` ile kapatılır. */
  hotkey?: boolean;
  texts?: Partial<IWidgetTexts>;
  /** Sonuç satırının markup'ını tamamen devral. */
  renderItem?: (item: IAiSearchQueryResult) => string | Element;
  /** Bir sonuca tıklanınca. `false` dönerse yönlendirme yapılmaz. */
  onSelect?: (item: IAiSearchQueryResult) => boolean | void;
}

export type TWidgetTheme = "light" | "dark" | "auto";

export interface IWidgetUi {
  open: (initialQuery?: string) => void;
  close: () => void;
  destroy: () => void;
  /** Temayı çalışırken değiştirir (pencere + `mount` ile basılan çubuk). */
  setTheme: (theme: TWidgetTheme) => void;
}

const ICON_FOLDER =
  '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M3 6h7l2 2h9v10H3z"/></svg>';
const ICON_TAG =
  '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M20 12 12 20 4 12V4h8z"/></svg>';

const SEARCH_ICON =
  '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/></svg>';

const resolveElements = (
  target: string | Element | Element[] | undefined,
): Element[] => {
  if (!target) return [];
  if (typeof target === "string")
    return Array.prototype.slice.call(document.querySelectorAll(target));
  if (Array.isArray(target)) return target;
  return [target];
};

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const fillTemplate = (template: string, values: Record<string, string>) =>
  template.replace(/\{(\w+)\}/g, (match, key) =>
    key in values ? values[key] : match,
  );

export const createWidgetUi = (options: IWidgetUiOptions): IWidgetUi => {
  const {
    client,
    productUrl,
    searchUrl,
    currency,
    locale,
    theme = "light",
    styles: withStyles = true,
    hotkey = true,
    renderItem,
    onSelect,
  } = options;

  const texts: IWidgetTexts = { ...DEFAULT_TEXTS, ...(options.texts || {}) };

  if (withStyles) injectStyles();

  const priceFormatter = (() => {
    try {
      return new Intl.NumberFormat(locale || undefined, {
        style: currency ? "currency" : "decimal",
        currency: currency || undefined,
        maximumFractionDigits: 2,
      });
    } catch {
      // Geçersiz locale/currency verilirse biçimlendirme yüzünden patlamayalım.
      return null;
    }
  })();

  const formatPrice = (value: number) =>
    priceFormatter ? priceFormatter.format(value) : String(value);

  /* --- DOM kurulumu ------------------------------------------------- */

  const dialog = document.createElement("dialog");
  dialog.className = "shs-dialog";
  dialog.setAttribute("aria-label", texts.placeholder);
  dialog.setAttribute("data-theme", theme);

  const supportsModal = typeof dialog.showModal === "function";
  if (!supportsModal) dialog.setAttribute("data-fallback", "");

  const listId = `shs-list-${Math.random().toString(36).slice(2, 8)}`;

  dialog.innerHTML =
    '<div class="shs-panel" role="none">' +
    '<div class="shs-header">' +
    SEARCH_ICON +
    `<input class="shs-input" type="search" role="combobox" autocomplete="off" autocapitalize="off" spellcheck="false" aria-expanded="false" aria-controls="${listId}" aria-autocomplete="list">` +
    `<button class="shs-btn" type="button" data-shs-close aria-label="${escapeHtml(texts.close)}">` +
    '<span class="shs-btn__esc">esc</span><span class="shs-btn__x" aria-hidden="true">×</span>' +
    "</button>" +
    "</div>" +
    '<div class="shs-body">' +
    // V1 düzeni: solda öneriler + gruplar, sağda ürünler. Tüm satırlar tek
    // listbox'ın seçenekleri; ürün listesi `.shs-list` sözleşmesini korur.
    `<div class="shs-columns" id="${listId}" role="listbox" hidden>` +
    '<div class="shs-side"></div>' +
    '<div class="shs-main"><div class="shs-section__title shs-main__title"></div>' +
    '<ul class="shs-list" role="none"></ul></div>' +
    "</div>" +
    '<div class="shs-state" hidden></div>' +
    "</div>" +
    '<div class="shs-footer">' +
    `<span>${escapeHtml(texts.hint)}</span>` +
    '<a class="shs-footer__all" hidden></a>' +
    "</div>" +
    "</div>";

  const panel = dialog.querySelector(".shs-panel") as HTMLElement;
  const input = dialog.querySelector(".shs-input") as HTMLInputElement;
  const columns = dialog.querySelector(".shs-columns") as HTMLElement;
  const side = dialog.querySelector(".shs-side") as HTMLElement;
  const mainTitle = dialog.querySelector(".shs-main__title") as HTMLElement;
  const list = dialog.querySelector(".shs-list") as HTMLUListElement;
  const state = dialog.querySelector(".shs-state") as HTMLElement;
  const allLink = dialog.querySelector(".shs-footer__all") as HTMLAnchorElement;
  input.placeholder = texts.placeholder;

  document.body.appendChild(dialog);

  /* --- durum -------------------------------------------------------- */

  /** Klavyeyle gezilen tek liste: öneriler → gruplar → ürünler. */
  interface INavEntry {
    el: HTMLElement;
    select: () => void;
  }
  let navEntries: INavEntry[] = [];
  let activeIndex = -1;
  let watchHandle: IWatchHandle | null = null;
  let previousFocus: Element | null = null;
  let previousOverflow = "";
  let destroyed = false;

  const setState = (message: string | null, variant?: "error") => {
    if (!message) {
      state.hidden = true;
      state.textContent = "";
      state.className = "shs-state";
      return;
    }
    state.hidden = false;
    state.textContent = message;
    state.className = variant === "error" ? "shs-state shs-state--error" : "shs-state";
  };

  const itemHref = (item: IAiSearchQueryResult) =>
    item.url ||
    (productUrl
      ? fillTemplate(productUrl, {
          code: encodeURIComponent(item.code || ""),
          id: encodeURIComponent(item.id || ""),
        })
      : "");

  const go = (href: string) => {
    window.location.href = href;
  };

  /** Metinle arama: `searchUrl` varsa oraya, yoksa kutuya yaz ve önerileri yenile. */
  const searchFor = (text: string) => {
    if (searchUrl) {
      go(fillTemplate(searchUrl, { q: encodeURIComponent(text) }));
      return;
    }
    input.value = text;
    watchHandle?.setQuery(text);
  };

  const selectProduct = (item: IAiSearchQueryResult, row: HTMLElement) => {
    if (onSelect && onSelect(item) === false) return;
    const href = itemHref(item);
    if (href) {
      go(href);
      return;
    }
    // `renderItem`'ın kendi `<a href>`'ine düş.
    const own = row.getAttribute("href") || row.querySelector("a")?.getAttribute("href");
    if (own) go(own);
  };

  const setActive = (index: number) => {
    const previous = navEntries[activeIndex]?.el;
    if (previous) {
      previous.classList.remove("shs-item--active");
      previous.setAttribute("aria-selected", "false");
    }
    activeIndex = index;
    const next = navEntries[index]?.el;
    if (next) {
      next.classList.add("shs-item--active");
      next.setAttribute("aria-selected", "true");
      input.setAttribute("aria-activedescendant", next.id);
      // `nearest`: listeyi gereksiz zıplatmaz.
      next.scrollIntoView({ block: "nearest" });
    } else {
      input.removeAttribute("aria-activedescendant");
    }
  };

  /** Bir seçeneği nav listesine kaydeder; id ve aria aynı yerden. */
  const register = (el: HTMLElement, select: () => void) => {
    const index = navEntries.length;
    el.id = `${listId}-${index}`;
    el.setAttribute("role", "option");
    el.setAttribute("aria-selected", "false");
    el.setAttribute("data-nav-index", String(index));
    navEntries.push({ el, select });
  };

  const sectionTitle = (text: string) => {
    const title = document.createElement("div");
    title.className = "shs-section__title";
    title.textContent = text;
    return title;
  };

  const optionRow = (iconHtml: string, labelHtml: string, metaHtml = "") => {
    const row = document.createElement("div");
    row.className = "shs-option";
    row.innerHTML =
      iconHtml +
      `<span class="shs-option__label">${labelHtml}</span>` +
      metaHtml;
    return row;
  };

  const renderProduct = (item: IAiSearchQueryResult, query: string) => {
    const li = document.createElement("li");
    li.setAttribute("role", "none");

    if (renderItem) {
      const custom = renderItem(item);
      if (typeof custom === "string") li.innerHTML = custom;
      else li.appendChild(custom);
      const firstChild = li.firstElementChild as HTMLElement | null;
      if (firstChild) register(firstChild, () => selectProduct(item, firstChild));
      return li;
    }

    const href = itemHref(item);
    const anchor = document.createElement(href ? "a" : "div");
    anchor.className = "shs-item";
    if (href) (anchor as HTMLAnchorElement).href = href;

    anchor.innerHTML =
      (item.image
        ? `<img class="shs-item__image" src="${escapeHtml(item.image)}" alt="" loading="lazy">`
        : '<span class="shs-item__placeholder"></span>') +
      '<span class="shs-item__text">' +
      `<span class="shs-item__title">${highlightHtml(item.title || "", query)}</span>` +
      // Alt satır mağaza ayarındaki kart alanı (marka/kategori/model); yoksa kod.
      `<span class="shs-item__code">${highlightHtml(item.subtitle || item.brand || item.code || "", query)}</span>` +
      "</span>" +
      '<span class="shs-item__meta">' +
      `<span class="shs-item__price">${escapeHtml(formatPrice(item.price))}</span>` +
      (item.list_price > item.price
        ? `<s class="shs-item__list-price">${escapeHtml(formatPrice(item.list_price))}</s>`
        : "") +
      "</span>";

    register(anchor, () => selectProduct(item, anchor));
    li.appendChild(anchor);
    return li;
  };

  /** Pencerenin içeriği: V1 düzeni. `view` null → içerik gizli. */
  const renderView = (view: IPanelView | null, query: string) => {
    navEntries = [];
    activeIndex = -1;
    input.removeAttribute("aria-activedescendant");
    side.textContent = "";
    list.textContent = "";

    const hasContent =
      !!view &&
      (view.suggestions.length > 0 ||
        view.groups.length > 0 ||
        view.products.length > 0);
    columns.hidden = !hasContent;
    input.setAttribute("aria-expanded", hasContent ? "true" : "false");
    if (!view || !hasContent) {
      allLink.hidden = true;
      return;
    }

    if (view.suggestions.length) {
      side.appendChild(sectionTitle(texts.suggestions));
      view.suggestions.forEach((item) => {
        const icon = item.image
          ? `<img class="shs-option__thumb" src="${escapeHtml(item.image)}" alt="" loading="lazy">`
          : `<span class="shs-option__icon">${SEARCH_ICON}</span>`;
        const meta =
          item.type !== "title"
            ? `<span class="shs-option__meta">${escapeHtml(item.typeLabel)}</span>`
            : "";
        const row = optionRow(icon, highlightHtml(item.text, query), meta);
        register(row, () => searchFor(item.text));
        side.appendChild(row);
      });
    }

    view.groups.forEach((group) => {
      side.appendChild(sectionTitle(group.label));
      group.items.forEach((item) => {
        const icon = `<span class="shs-option__icon shs-option__icon--plain">${group.type === "category" ? ICON_FOLDER : ICON_TAG}</span>`;
        const row = optionRow(
          icon,
          highlightHtml(item.name, query),
          `<span class="shs-option__count">${escapeHtml(String(item.count))}</span>`,
        );
        register(row, () => searchFor(item.name));
        side.appendChild(row);
      });
    });
    side.hidden = side.childElementCount === 0;

    mainTitle.textContent = view.browsing ? texts.featured : texts.products;
    view.products.forEach((item) => list.appendChild(renderProduct(item, query)));

    // Öneri/grup var, ürün yok (çoğunlukla yazım hatası): kolon boş kalmasın,
    // bilgi metni. Gezinmeye kaydedilmez; ek istek atılmaz.
    if (!view.products.length && !view.browsing) {
      const empty = document.createElement("li");
      empty.className = "shs-main__empty";
      empty.setAttribute("role", "none");
      empty.innerHTML =
        `<p>${escapeHtml(fillTemplate(texts.noProducts, { q: query }))}</p>` +
        `<p class="shs-main__empty-hint">${escapeHtml(texts.noProductsHint)}</p>`;
      list.appendChild(empty);
    }

    // Widget'ın eski davranışı: ilk ürün aktif (Enter onu açar). Öneri
    // kolonu dolu olduğunda bile ürün aktif — mağazada aranan çoğunlukla ürün.
    const firstProduct = navEntries.findIndex((entry) =>
      list.contains(entry.el),
    );
    if (firstProduct >= 0) setActive(firstProduct);

    // Sonuç yokken "Tüm sonuçları gör (0)" boş bir sayfaya götürür.
    if (searchUrl && view.found > 0) {
      allLink.hidden = false;
      allLink.href = fillTemplate(searchUrl, { q: encodeURIComponent(query) });
      allLink.textContent = `${texts.allResults} (${view.found})`;
    } else {
      allLink.hidden = true;
    }
  };

  const errorTextFor = (error: unknown) => {
    if (error instanceof AiSearchQueryError) {
      if (error.isRateLimited) return texts.errorRateLimit;
      // Geçersiz anahtar, başka alan adından kurulum ya da biten lisans: hepsi
      // mağazanın sorunu, ziyaretçiye aynı mesaj.
      if (error.isAuthError || error.isOriginError || error.isLicenceError)
        return texts.errorAuth;
      if (error.isBillingError) return texts.errorBilling;
    }
    return texts.errorGeneric;
  };

  /** Yazmadan açılış: boş sorgulu arama (örnek başına bir kez). */
  const showBrowse = () => {
    client
      .browse()
      .then((data) => {
        // Bu arada kullanıcı yazmaya başladıysa açılış görünümü basılmaz.
        if (destroyed || !dialog.open || input.value.trim()) return;
        setState(null);
        renderView(browseView(data), "");
      })
      .catch(() => {
        // Açılış verisi süs; hata gösterme, kutu boş kalsın.
        if (!input.value.trim()) renderView(null, "");
      });
  };

  const handleState = (searchState: SearchState) => {
    if (searchState === "loading") setState(texts.loading);
    if (searchState === "idle") setState(null);
  };

  /* --- açılış / kapanış --------------------------------------------- */

  const open = (initialQuery?: string) => {
    if (destroyed || dialog.open) return;

    previousFocus = document.activeElement;
    previousOverflow = document.documentElement.style.overflow;
    // showModal() sayfayı inert yapıyor ama bazı tarayıcılarda arka plan
    // hâlâ kaydırılabiliyor; scroll'u burada kilitliyoruz.
    document.documentElement.style.overflow = "hidden";

    if (supportsModal) dialog.showModal();
    else dialog.setAttribute("open", "");

    watchHandle = client.watchSuggest({
      onState: handleState,
      onSuggest: ({ query, data }) => {
        // Kısa sorgu (servise gidilmedi): açılış görünümüne dön.
        if (!data) {
          showBrowse();
          return;
        }
        const view = suggestView(data);
        const empty =
          !view.suggestions.length && !view.groups.length && !view.products.length;
        setState(empty ? texts.empty : null);
        renderView(view, query);
      },
      onError: (error) => {
        renderView(null, "");
        setState(errorTextFor(error), "error");
      },
    });

    if (initialQuery) {
      input.value = initialQuery;
      watchHandle.setQuery(initialQuery);
    } else {
      showBrowse();
    }
    // Odak, açılış animasyonu/inert geçişi otursun diye bir tick sonra.
    setTimeout(() => input.focus(), 0);
  };

  /**
   * Kapanış temizliği. **Ayrı fonksiyon olmak zorunda:** `<dialog>` Esc ile
   * kendi kendine kapanıyor ve o yolda bizim `close()`'umuz hiç çalışmıyor.
   * Bu yüzden temizlik `close` OLAYINA bağlı — hem Esc hem düğme hem backdrop
   * aynı yerden geçsin.
   *
   * İdempotent: `watchHandle`/`previousFocus` null'lanıyor, iki kez çağrılırsa
   * ikinci çağrı hiçbir şey yapmıyor.
   */
  const cleanupAfterClose = () => {
    watchHandle?.destroy();
    watchHandle = null;
    document.documentElement.style.overflow = previousOverflow;
    if (previousFocus instanceof HTMLElement) previousFocus.focus();
    previousFocus = null;
  };

  const close = () => {
    if (!dialog.open) return;
    if (supportsModal) {
      // `close` olayı tetiklenecek, temizliği o üstlenir.
      dialog.close();
    } else {
      dialog.removeAttribute("open");
      // Fallback modda native `close` olayı yok — elle çağır.
      cleanupAfterClose();
    }
  };

  /* --- olaylar ------------------------------------------------------ */

  const onInput = () => {
    watchHandle?.setQuery(input.value);
    if (!input.value) setState(null);
  };


  const onKeyDown = (event: KeyboardEvent) => {
    const count = navEntries.length;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (count) setActive((activeIndex + 1) % count);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      if (count) setActive((activeIndex - 1 + count) % count);
    } else if (event.key === "Enter") {
      if (activeIndex >= 0 && navEntries[activeIndex]) {
        event.preventDefault();
        navEntries[activeIndex].select();
      } else if (searchUrl && input.value.trim()) {
        event.preventDefault();
        window.location.href = fillTemplate(searchUrl, {
          q: encodeURIComponent(input.value.trim()),
        });
      }
    } else if (event.key === "Escape" && !supportsModal) {
      // showModal() Esc'yi kendi hallediyor; fallback modda biz kapatıyoruz.
      event.preventDefault();
      close();
    } else if (event.key === "Tab" && !supportsModal) {
      // Fallback modda odak tuzağı yok — panel içinde döndürüyoruz.
      const focusables = panel.querySelectorAll<HTMLElement>(
        'input,button,a[href],[tabindex]:not([tabindex="-1"])',
      );
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  };

  const onListClick = (event: Event) => {
    const target = (event.target as Element)?.closest?.("[data-nav-index]");
    if (!target) return;
    const entry = navEntries[Number(target.getAttribute("data-nav-index"))];
    if (!entry) return;
    // Ürün `<a href>`'i varsa tarayıcı değil biz yönlendiriyoruz (onSelect
    // kancası çalışsın); öneri/grup satırlarında zaten link yok.
    event.preventDefault();
    entry.select();
  };

  /** Fareyle üzerine gelmek de aktif yapar — klavye ile aynı vurgu. */
  const onListMove = (event: Event) => {
    const target = (event.target as Element)?.closest?.("[data-nav-index]");
    if (!target) return;
    const index = Number(target.getAttribute("data-nav-index"));
    if (index !== activeIndex) setActive(index);
  };

  /** Backdrop'a tıklayınca kapat: `<dialog>` tıklaması panelin dışına düşerse. */
  const onDialogClick = (event: MouseEvent) => {
    if (!panel.contains(event.target as Node)) close();
  };

  const onHotkey = (event: KeyboardEvent) => {
    if (!hotkey) return;
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
      event.preventDefault();
      if (dialog.open) close();
      else open();
    }
  };

  input.addEventListener("input", onInput);
  input.addEventListener("keydown", onKeyDown);
  columns.addEventListener("click", onListClick);
  columns.addEventListener("mousemove", onListMove);
  dialog.addEventListener("click", onDialogClick);
  dialog.addEventListener("close", cleanupAfterClose);
  dialog
    .querySelector("[data-shs-close]")
    ?.addEventListener("click", () => close());
  document.addEventListener("keydown", onHotkey);

  /* --- tetikleyiciler ----------------------------------------------- */

  const triggerCleanups: Array<() => void> = [];

  const bindTrigger = (element: Element) => {
    const handleOpen = (event: Event) => {
      event.preventDefault();
      // Mağazanın kendi input'u odaklanmasın/form submit olmasın: odağı hemen
      // bırakıp modalı açıyoruz. DocSearch'ün yaptığı da bu.
      if (element instanceof HTMLElement) element.blur();
      const existing =
        element instanceof HTMLInputElement ? element.value : undefined;
      open(existing);
    };
    element.addEventListener("click", handleOpen);
    element.addEventListener("focus", handleOpen);
    triggerCleanups.push(() => {
      element.removeEventListener("click", handleOpen);
      element.removeEventListener("focus", handleOpen);
    });
  };

  /**
   * Widget'ın **kendi DOM'u tetikleyici olamaz.**
   *
   * `trigger: "input[type=search]"` gibi genel bir seçici, modalın kendi
   * `.shs-input`'unu da yakalıyordu (dialog `document.body`'ye bu satırdan önce
   * ekleniyor). Sonuç: kullanıcı arama alanına tıklayınca `handleOpen`
   * çalışıyor, `element.blur()` odağı anında bırakıyor ve alan **yazılamaz**
   * hale geliyordu — pencere açılıyor ama içine yazı girilemiyor.
   */
  const isOwnDom = (element: Element) => dialog.contains(element);

  resolveElements(options.trigger)
    .filter((element) => !isOwnDom(element))
    .forEach(bindTrigger);

  /** Mac'te ⌘K, diğerlerinde Ctrl K göster. */
  const hotkeyLabel =
    typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || "")
      ? "⌘K"
      : "Ctrl K";

  /** `setTheme` için: `data-theme` taşıyan kendi çubuklarımız. */
  const themedButtons: HTMLElement[] = [];

  resolveElements(options.mount)
    .filter((container) => !isOwnDom(container))
    .forEach((container) => {
      const button = document.createElement("button");
      button.type = "button";
      button.className = "shs-trigger";
      button.setAttribute("data-theme", theme);
      themedButtons.push(button);
      if (hotkey) button.setAttribute("data-hotkey", "1");
      button.innerHTML =
        SEARCH_ICON +
        `<span class="shs-trigger__label">${escapeHtml(texts.placeholder)}</span>` +
        `<span class="shs-trigger__kbd">${escapeHtml(hotkeyLabel)}</span>`;
      container.appendChild(button);
      bindTrigger(button);
      triggerCleanups.push(() => button.remove());
    });

  const setTheme = (next: TWidgetTheme) => {
    if (next !== "light" && next !== "dark" && next !== "auto") return;
    dialog.setAttribute("data-theme", next);
    themedButtons.forEach((button) => button.setAttribute("data-theme", next));
  };

  return {
    open,
    close,
    setTheme,
    destroy: () => {
      destroyed = true;
      close();
      document.removeEventListener("keydown", onHotkey);
      triggerCleanups.forEach((cleanup) => cleanup());
      dialog.remove();
    },
  };
};
